import { homeFaqs } from "@/data/pageFaqs";
import { toolBySlug } from "@/data/tools";
import { PageFaqs } from "@/components/site/PageFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import { m } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { BookFitCall } from "@/components/site/BookFitCall";
import { canonicalLinks, ogUrl, SITE_URL, ogImage, jsonLd, webPageSchema, ORG_DESCRIPTION } from "@/lib/seo";
import { Hero } from "@/components/site/Hero";
import { CareerStatsBand } from "@/components/site/CareerStatsBand";
import { ClientLogos } from "@/components/site/ClientLogos";
import { WhyAjility } from "@/components/site/WhyAjility";
import { Services } from "@/components/site/Services";
import { TrackRecord } from "@/components/site/TrackRecord";
import { Testimonials } from "@/components/site/Testimonials";
import { GeographicReach } from "@/components/site/GeographicReach";
import { SixPillars } from "@/components/diagrams/SixPillars";
import { PhotoBand } from "@/components/site/PhotoBand";
import { CalendlyInlineWidget } from "@/components/site/CalendlyInlineWidget";
import { BookingIntro } from "@/components/site/BookingIntro";
import workshopImg from "@/assets/photos/workshop-floor.webp";

const HOME_DESCRIPTION =
  "Senior, hands-on fractional COO leadership for founder-led Australian businesses turning over $3M to $30M: operating structure, clear numbers, execution.";

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${SITE_URL}/#faq`,
  mainEntity: homeFaqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      {
        title: "Ajility | Fractional COO and GM, Sydney and Australia-wide",
      },
      {
        name: "description",
        content: HOME_DESCRIPTION,
      },
      {
        property: "og:title",
        content: "Ajility | Fractional COO and GM, Sydney and Australia-wide",
      },
      {
        property: "og:description",
        content: HOME_DESCRIPTION,
      },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Ajility: Fractional COO & Operations Partner for Founders" },
      { name: "twitter:description", content: HOME_DESCRIPTION },
      ...ogImage("home", "Ajility: operations leadership for founder-led businesses"),
      ogUrl("/"),
    ],
    links: [
      ...canonicalLinks("/"),
    ],
    scripts: [
      jsonLd(
        webPageSchema({ image: "home",
          path: "/",
          name: "Ajility: Fractional COO & Operations Partner for Founders",
          description: HOME_DESCRIPTION,
          breadcrumb: false,
        }),
      ),
      {
        type: "application/ld+json",
        children: JSON.stringify(faqSchema),
      },
    ],
  }),
  component: HomePage,
});

function HomePage() {
  return (
    <>
      <Hero />
      <CareerStatsBand />
      <ClientLogos />
      <WhyAjility />
      <PhotoBand
        src={workshopImg}
        alt="Two workers assembling components at a bench on an Australian light-industrial workshop floor"
        surface="background"
        className="pb-14"
      />
      <WhatThisIsNot />
      <Services />
      <StatBand />
      <TrackRecord />
      <Testimonials />
      <GeographicReach />
      <ScanBand />
      <ToolsStrip />
      <section className="bg-background py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <h2 className="font-serif text-3xl text-foreground sm:text-4xl">Questions founders ask</h2>
          <p className="mt-5 text-lg leading-relaxed text-charcoal">Plain answers to the questions owners of $3M to $30M businesses actually type, written from the operating seat.</p>
          <ul className="mt-8 list-disc space-y-4 pl-5 text-charcoal">
            <li><Link to="/insights/$slug" params={{ slug: "working-partner-vs-silent-partner" }} className="underline underline-offset-4">Working partner vs silent partner: how to split pay and profit fairly</Link></li>
            <li><Link to="/insights/$slug" params={{ slug: "hire-a-general-manager-or-a-fractional-one" }} className="underline underline-offset-4">Should I hire a general manager or a fractional one?</Link></li>
            <li><Link to="/insights/$slug" params={{ slug: "how-to-step-back-from-sales-in-your-own-business" }} className="underline underline-offset-4">How to step back from sales in your own business</Link></li>
            <li><Link to="/insights/$slug" params={{ slug: "how-to-restructure-a-small-business-group-with-shared-owners" }} className="underline underline-offset-4">How to restructure a small business group with shared owners</Link></li>
            <li><Link to="/insights/$slug" params={{ slug: "13-week-cash-flow-forecast" }} className="underline underline-offset-4">The 13-week cash flow forecast: what it is and how to build one</Link></li>
            <li><Link to="/insights/$slug" params={{ slug: "revenue-leakage" }} className="underline underline-offset-4">Revenue leakage: where the money goes missing between the quote and the bank</Link></li>
            <li><Link to="/insights/$slug" params={{ slug: "commission-and-bonus-structure-design" }} className="underline underline-offset-4">Commission and bonus structure design for a small business</Link></li>
            <li><Link to="/insights/$slug" params={{ slug: "payday-super-cash-flow" }} className="underline underline-offset-4">Payday Super: what it does to a small business's cash flow</Link></li>
            <li><Link to="/insights/$slug" params={{ slug: "card-surcharge-ban-pricing" }} className="underline underline-offset-4">The card surcharge ban: how to price the fee back in without losing customers</Link></li>
            <li><Link to="/insights/$slug" params={{ slug: "ai-adoption-small-business" }} className="underline underline-offset-4">AI adoption in a small business: why the team won't use the tools you paid for</Link></li>
            <li><Link to="/insights/$slug" params={{ slug: "small-business-succession-planning" }} className="underline underline-offset-4">Small business succession planning: getting the business ready to sell or hand over</Link></li>
            <li><Link to="/insights/$slug" params={{ slug: "security-of-payment-act-subcontractors" }} className="underline underline-offset-4">Security of Payment Act for subcontractors: getting paid when a builder slows down</Link></li>
          </ul>
        </div>
      </section>
      <PageFaqs faqs={homeFaqs} />
      <FinalCta />
    </>
  );
}

const notCards = [
  {
    title: "Not a consultant",
    body: "No deck and disappear. Ajility sits in the leadership meeting, owns the scorecard with you and stays until the rhythm runs itself.",
  },
  {
    title: "Not a part-time hire",
    body: "No payroll, no super, no leave, no recruitment fee. Senior COO leadership, plus GST, committed a quarter at a time.",
  },
  {
    title: "Not a permanent cost",
    body: "Every engagement is reviewed at the 90-day boundary. Install what the roadmap calls for, drop it when it runs itself.",
  },
];

function WhatThisIsNot() {
  return (
    <section className="bg-background py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <span className="eyebrow">What this is not</span>
          <h2 className="mt-5 font-serif text-4xl leading-tight text-foreground sm:text-5xl text-balance">
            Fractional COO leadership, not consulting or a permanent hire.
          </h2>
        </div>
        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {notCards.map((card) => (
            <article key={card.title} className="card-panel p-8 sm:p-10">
              <h3 className="font-serif text-2xl text-foreground">{card.title}</h3>
              <p className="mt-4 leading-relaxed text-charcoal">{card.body}</p>
            </article>
          ))}
        </div>
        <div className="mt-14 text-center">
          <Link
            to="/packages"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary"
          >
            See what each step costs
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

const stats = [
  {
    figure: "118%",
    context:
      "net profit growth over four years in a transport and logistics business, earlier in Adam's career, in-house.",
    to: "/insights/$slug",
    slug: "transport-118-percent",
  },
  {
    figure: "~$1M",
    context: "of annualised overhead removed in the first 90 days of a mining services engagement",
    to: "/insights/$slug",
    slug: "mining-90-days",
  },
  {
    figure: "40%",
    context:
      "reduction in overhead as a share of sales in an electrical contracting business, earlier in Adam's career, in-house.",
    to: "/insights/$slug",
    slug: "electrical-margins",
  },
];

function StatBand() {
  return (
    <section className="bg-background pb-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-px overflow-hidden rounded-3xl border border-border bg-border md:grid-cols-3">
          {stats.map((s, i) => (
            <m.article
              key={s.figure + i}
              initial={{ y: 24 }}
              whileInView={{ y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group relative bg-card p-10 transition hover:bg-mist"
            >
              <p className="font-serif text-5xl text-foreground">{s.figure}</p>
              <p className="mt-4 leading-relaxed text-charcoal">{s.context}</p>
              <Link
                to={s.to}
                params={{ slug: s.slug }}
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary"
              >
                Read the case study
                <ArrowRight className="h-4 w-4" />
              </Link>
            </m.article>
          ))}
        </div>
      </div>
    </section>
  );
}

const HOME_TOOLS = ["cash-runway-calculator", "price-change-impact-calculator", "time-and-accountability-grid"] as const;

function ToolsStrip() {
  return (
    <section className="bg-mist pb-24" aria-labelledby="home-tools-h">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <span className="eyebrow">Free tools</span>
        <h2 id="home-tools-h" className="mt-4 font-serif text-2xl text-foreground sm:text-3xl">Or answer one question now</h2>
        <ul className="mt-8 grid gap-6 md:grid-cols-3">
          {HOME_TOOLS.map((slug) => {
            const t = toolBySlug(slug);
            return (
              <li key={slug}>
                <Link to={t.path} className="card-panel card-interactive flex h-full flex-col">
                  <h3 className="font-serif text-xl text-midnight">{t.name}</h3>
                  <p className="mt-2 flex-1 text-charcoal">{t.oneLine}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-midnight">
                    Try the tool <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

function ScanBand() {
  return (
    <section className="bg-mist py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <span className="eyebrow">Operating Scan</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl text-balance">
            Operating Scan: 21 plain statements and a provisional score
          </h2>
          <p className="mt-4 leading-relaxed text-charcoal">
            Answer from Not us to Always in about six minutes. You see the weakest areas, then choose whether you want a conversation.
          </p>
        </div>
        <div className="mt-12">
          <SixPillars />
        </div>
        <div className="mt-10">
          <Link
            to="/scan"
            className="inline-flex items-center gap-2 rounded-full bg-midnight px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-midnight-deep"
          >
            Take the Operating Scan
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="bg-midnight py-24 text-frost lg:py-32">
      <div className="mx-auto max-w-4xl px-6 text-center">
        <p className="text-sm font-semibold uppercase tracking-widest text-silver">
          Get started
        </p>
        <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl">
          Ready to install structure and execution discipline?
        </h2>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-frost/80">
          Book a free 30-minute fit call. Tell Ajility what is getting stuck and get an honest view on whether the fit is right.
        </p>
        <div className="mt-10 flex flex-col items-start justify-center gap-4 sm:flex-row">
          <BookFitCall
            placement="home_final_cta"
            buttonClassName="inline-flex h-12 items-center gap-2 rounded-full bg-frost px-7 text-base font-semibold text-midnight-deep transition hover:bg-white"
            noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
          >
            Book a fit call
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </BookFitCall>
          <Link
            to="/packages"
            className="inline-flex h-12 items-center gap-2 rounded-full border border-white/20 px-7 text-base font-semibold text-frost transition hover:border-steel hover:text-frost"
          >
            View engagement packages
          </Link>
        </div>
        <details className="mx-auto mt-10 max-w-2xl text-left">
          <summary className="cursor-pointer list-none text-center text-sm font-semibold text-frost underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-frost">
            Or pick a time now
          </summary>
          <BookingIntro />
          <CalendlyInlineWidget location="home_final_cta" />
        </details>
      </div>
    </section>
  );
}
